import { useEffect, useState } from 'react';
import type { ClientConfig } from '@archipro-website/config';
import { useRootData } from '@modules/root';
import { FacebookPixelsHead } from './FacebookTracker';
import { LinkedInTrackerHead } from './LinkedInTracker';
import { PinterestTrackerHead } from './PinterestTracker';
import { OutbrainTrackerHead } from './Outbrain';
import {
    hasSaleOrSharingOptOut,
    shouldAllowThirdPartySharing,
} from './allow-third-party-sharing';
import { isConfiguredPixelId } from './is-configured-pixel-id';
import { hasSaleSharingOptOutCookie } from '@modules/privacy/sale-sharing-opt-out-cookie';

interface NavigatorWithGPC extends Navigator {
    globalPrivacyControl?: boolean;
}

/**
 * Injects the `<script>` halves of advertising trackers on the client only,
 * after checking the Global Privacy Control browser signal and the explicit
 * sale/sharing opt-out from either source: the signed-in member's preference or
 * the opt-out cookie. The matching `<noscript><img>` halves are SSR-rendered in
 * `BodyPixels` from the request headers, root data and cookie.
 *
 * `allowAdvertising` starts false and resolves in `useEffect` so both signals
 * are read from the browser once after hydration (never during SSR). Advertising scripts
 * therefore load one tick later than when they lived in `HeadPixels`. Outbrain
 * has no `<noscript>` fallback, so no-JS requests get no Outbrain coverage;
 * Facebook, Pinterest, and LinkedIn still emit `<noscript><img>` pixels from
 * `BodyPixels`.
 */
export const ClientAdvertisingPixels = ({
    facebookPixelArchiproId,
    facebookPixelClientId,
    pinterestTagArchiproId,
    pinterestTagClientId,
    linkedinPartnerId,
    outbrainId,
}: Pick<
    ClientConfig['pixels'],
    | 'facebookPixelArchiproId'
    | 'facebookPixelClientId'
    | 'pinterestTagArchiproId'
    | 'pinterestTagClientId'
    | 'linkedinPartnerId'
    | 'outbrainId'
>) => {
    const { localisation, user } = useRootData();
    const { honourGlobalPrivacyControl } = localisation.countryConfig;
    const memberOptedOut = hasSaleOrSharingOptOut(user);
    const [allowAdvertising, setAllowAdvertising] = useState(false);

    useEffect(() => {
        const gpcEnabled =
            (navigator as NavigatorWithGPC).globalPrivacyControl === true;
        // Both signals are read once per document. This component is mounted in
        // the root layout, so it does not remount on soft navigation and the
        // effect does not re-run; nothing here reacts to the opt-out cookie
        // being set mid-session. That is why the opt-out form reloads the page
        // on success rather than relying on a re-render.
        setAllowAdvertising(
            shouldAllowThirdPartySharing(
                honourGlobalPrivacyControl,
                gpcEnabled,
                memberOptedOut || hasSaleSharingOptOutCookie(),
            ),
        );
    }, [honourGlobalPrivacyControl, memberOptedOut]);

    if (!allowAdvertising) {
        return null;
    }

    const hasFacebookPixel =
        isConfiguredPixelId(facebookPixelArchiproId) ||
        isConfiguredPixelId(facebookPixelClientId);
    const hasPinterestPixel =
        isConfiguredPixelId(pinterestTagArchiproId) ||
        isConfiguredPixelId(pinterestTagClientId);

    return (
        <>
            {hasFacebookPixel && (
                <FacebookPixelsHead
                    pixel={facebookPixelArchiproId}
                    pixelClient={facebookPixelClientId}
                />
            )}
            {hasPinterestPixel && (
                <PinterestTrackerHead
                    pixelArchipro={pinterestTagArchiproId}
                    pixelClient={pinterestTagClientId}
                />
            )}
            {isConfiguredPixelId(linkedinPartnerId) && (
                <LinkedInTrackerHead partnerId={linkedinPartnerId} />
            )}
            {isConfiguredPixelId(outbrainId) && (
                <OutbrainTrackerHead id={outbrainId} />
            )}
        </>
    );
};
